import { Capacitor } from '@capacitor/core';
import { alwaysLocationService } from '../services/alwaysLocationService';

// Notification Helper Functions

export interface NotificationSettings {
  settings: Array<{
    id: string;
    enabled: boolean;
  }>;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
}

/**
 * Check if notifications are enabled
 */
export function areNotificationsEnabled(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}

/**
 * Check if location tracking is enabled
 */
export function isLocationEnabled(): boolean {
  return 'geolocation' in navigator;
}

/**
 * Get notification settings from localStorage
 */
export function getNotificationSettings(): NotificationSettings | null {
  const saved = localStorage.getItem('driverNotificationSettings');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (error) {
      console.error('Error parsing notification settings:', error);
      return null;
    }
  }
  return null;
}

/**
 * Check if a specific notification type is enabled
 */
export function isNotificationTypeEnabled(type: string): boolean {
  const settings = getNotificationSettings();
  if (!settings) return true; // Default to enabled if no settings
  
  const notifSetting = settings.settings.find(s => s.id === type);
  return notifSetting ? notifSetting.enabled : true;
}

/**
 * Send a notification to the driver
 */
export function sendDriverNotification(
  title: string,
  body: string,
  type: 'new-offers' | 'bid-updates' | 'bid-approval' | 'bid-rejection' | 'system' = 'system',
  onClick?: () => void
): void {
  // Check if this notification type is enabled
  if (!isNotificationTypeEnabled(type)) {
    console.log(`Notification type ${type} is disabled`);
    return;
  }

  // Check if notifications are enabled
  if (!areNotificationsEnabled()) {
    console.log('Notifications are not enabled');
    return;
  }

  const settings = getNotificationSettings();
  
  try {
    const notification = new Notification(title, {
      body,
      icon: '/logo.png',
      badge: '/logo.png',
      tag: 'yuk-ortagi-' + Date.now(),
      requireInteraction: false,
      silent: settings ? !settings.soundEnabled : false,
    });

    // Vibrate if enabled
    if (settings?.vibrationEnabled && 'vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]);
    }

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (onClick) onClick();
    };

    // Auto close after 5 seconds
    setTimeout(() => notification.close(), 5000);

    // Save notification to localStorage for history
    saveNotificationToHistory({
      id: Date.now().toString(),
      type,
      title,
      message: body,
      time: new Date().toISOString(),
      read: false,
    });
  } catch (error) {
    console.error('Error sending notification:', error);
  }
}

/**
 * Save notification to localStorage history
 */
function saveNotificationToHistory(notification: any): void {
  try {
    const history = localStorage.getItem('driverNotifications');
    const notifications = history ? JSON.parse(history) : [];
    notifications.unshift(notification);
    
    // Keep only last 50 notifications
    if (notifications.length > 50) {
      notifications.splice(50);
    }
    
    localStorage.setItem('driverNotifications', JSON.stringify(notifications));
  } catch (error) {
    console.error('Error saving notification to history:', error);
  }
}

/**
 * Get current driver location
 */
export function getDriverLocation(): Promise<{
  latitude: number;
  longitude: number;
  accuracy: number;
  speed: number | null;
  heading: number | null;
  timestamp: string;
} | null> {
  return new Promise((resolve, reject) => {
    if (!isLocationEnabled()) {
      reject(new Error('Location is not enabled'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed,
          heading: position.coords.heading,
          timestamp: new Date().toISOString(),
        };
        
        // Save to localStorage
        localStorage.setItem('driverLocation', JSON.stringify(locationData));
        
        resolve(locationData);
      },
      (error) => {
        console.error('Location error:', error);
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  });
}

/**
 * Get last saved location from localStorage
 */
export function getLastKnownLocation(): {
  latitude: number;
  longitude: number;
  accuracy?: number;
  speed?: number | null;
  heading?: number | null;
  timestamp: string;
} | null {
  const saved = localStorage.getItem('driverLocation');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (error) {
      console.error('Error parsing location:', error);
      return null;
    }
  }
  return null;
}

/**
 * Start continuous location tracking
 */
export function startLocationTracking(): number | null {
  if (!isLocationEnabled()) {
    console.error('Location is not enabled');
    return null;
  }

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      const locationData = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        speed: position.coords.speed,
        heading: position.coords.heading,
        timestamp: new Date().toISOString(),
      };
      
      localStorage.setItem('driverLocation', JSON.stringify(locationData));
      
      // Dispatch custom event for location updates
      window.dispatchEvent(new CustomEvent('driverLocationUpdate', { 
        detail: locationData 
      }));
    },
    (error) => {
      console.error('Location tracking error:', error);
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    }
  );

  localStorage.setItem('driverLocationWatchId', watchId.toString());
  return watchId;
}

/**
 * Stop location tracking
 */
export function stopLocationTracking(): void {
  const watchIdStr = localStorage.getItem('driverLocationWatchId');
  if (watchIdStr) {
    const watchId = parseInt(watchIdStr, 10);
    navigator.geolocation.clearWatch(watchId);
    localStorage.removeItem('driverLocationWatchId');
  }
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if ('Notification' in window) {
    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error('Notification permission error:', error);
      return 'denied';
    }
  }
  return 'denied';
}

/**
 * Request location permission
 */
export async function requestLocationPermission(): Promise<boolean> {
  try {
    if (Capacitor.isNativePlatform()) {
      await alwaysLocationService.requestAlwaysIfAvailable();
    }
    const location = await getDriverLocation();
    return location !== null;
  } catch (error) {
    console.error('Location permission error:', error);
    return false;
  }
}
